import type { McpConnector, McpConnectorPreset, McpGatewayTool } from "@/services/mcp-gateway";
import { isConnectorUsable } from "@/services/mcp-gateway";

export type ProviderCardState = "not_connected" | "connected" | "needs_reconnect";

export type ProviderCard = {
  key: string;
  title: string;
  description: string;
  /** Null for a connection whose provider is no longer offered (e.g. a removed preset). */
  preset: McpConnectorPreset | null;
  connectors: McpConnector[];
  state: ProviderCardState;
  readOnly: boolean;
  toolCount: number;
};

function cardState(connectors: McpConnector[]): ProviderCardState {
  if (!connectors.length) return "not_connected";
  return connectors.some((c) => isConnectorUsable(c.status)) ? "connected" : "needs_reconnect";
}

/**
 * One card per provider, carrying the member's own connection(s) for it. Presets come first in the
 * gateway's order; connections whose provider is no longer a preset follow, so they stay manageable.
 */
export function buildProviderCards(presets: McpConnectorPreset[], connectors: McpConnector[]): ProviderCard[] {
  const byProvider = new Map<string, McpConnector[]>();
  for (const c of connectors) byProvider.set(c.provider, [...(byProvider.get(c.provider) ?? []), c]);

  const card = (key: string, title: string, description: string, p: McpConnectorPreset | null): ProviderCard => {
    const own = byProvider.get(key) ?? [];
    return {
      key,
      title,
      description,
      preset: p,
      connectors: own,
      state: cardState(own),
      readOnly: own.some((c) => c.read_only),
      toolCount: own.reduce((sum, c) => sum + c.tool_count, 0),
    };
  };

  const cards = presets.map((p) =>
    card(p.key, p.title, p.description || (p.key.startsWith("custom-") ? "Added by your org admin." : ""), p),
  );
  const offered = new Set<string>(presets.map((p) => p.key));
  for (const [key, own] of byProvider) {
    if (!offered.has(key)) cards.push(card(key, own[0].name, "", null));
  }
  return cards;
}

/**
 * Tools on read-only connections that the gateway would refuse. `allowedByConnector` holds, per
 * read-only connection, the tool ids the gateway's own `/connectors/{id}/tools` still lists — so the
 * rule lives in one place (the gateway) instead of a copy of its verb lists here.
 */
export function blockedToolIds(tools: McpGatewayTool[], allowedByConnector: Map<string, Set<string>>): Set<string> {
  const blocked = new Set<string>();
  for (const t of tools) {
    const allowed = allowedByConnector.get(t.connector_id);
    if (allowed && !allowed.has(t.id)) blocked.add(t.id);
  }
  return blocked;
}

/** Usable tools first, so a read-only group doesn't open on a wall of blocked rows. Stable otherwise. */
export function allowedFirst(tools: McpGatewayTool[], blocked: Set<string>): McpGatewayTool[] {
  return [...tools.filter((t) => !blocked.has(t.id)), ...tools.filter((t) => blocked.has(t.id))];
}

export type ToolGroup ={ connectorId: string; title: string; connector: McpConnector | null; tools: McpGatewayTool[] };

export function groupToolsByConnector(tools: McpGatewayTool[], connectors: McpConnector[]): ToolGroup[] {
  const groups = new Map<string, ToolGroup>();
  for (const c of connectors) groups.set(c.id, { connectorId: c.id, title: c.name, connector: c, tools: [] });
  for (const t of tools) {
    let group = groups.get(t.connector_id);
    if (!group) {
      group = { connectorId: t.connector_id, title: t.connector_name || t.provider || "MCP", connector: null, tools: [] };
      groups.set(t.connector_id, group);
    }
    group.tools.push(t);
  }
  return [...groups.values()].filter((g) => g.tools.length);
}

/** `clickup_get_task` under "Clickup" → "Get task"; `getJiraIssue` → "Get jira issue". */
export function humanToolName(name: string, providerTitle?: string | null): string {
  const prefix = (providerTitle ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
  let rest = name;
  if (prefix) {
    const lower = name.toLowerCase();
    for (const sep of ["_", "-", "."]) {
      if (lower.startsWith(prefix + sep) && name.length > prefix.length + 1) {
        rest = name.slice(prefix.length + 1);
        break;
      }
    }
  }
  const words = rest
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_\-.]+/g, " ")
    .trim()
    .toLowerCase();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : name;
}
